/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Адаптер history v4 поверх TanStack Router (docs/MIGRATION_ROUTER.md, Фаза 1).
 *
 * Легаси-приложение (react-router v5 + 140 использований useHistory)
 * продолжает работать со знакомым интерфейсом history, но реальным
 * источником URL-состояния является TanStack Router: push/replace
 * делегируются в router.history (@tanstack/history), listen — в его
 * subscribe. Так у обоих роутеров ОДНО состояние URL и навигация из
 * любой зоны видна обоим.
 *
 * Семантика v4 воспроизведена сознательно, включая тонкости:
 *  - push({ hash }) БЕЗ pathname: pathname берётся текущий, search
 *    СБРАСЫВАЕТСЯ (так делает history v4 — bug-compatible);
 *  - push('#/service/8') (hash-only строка) — то же самое;
 *  - location.search/hash — сырые строки с '?'/'#' (или '');
 *  - state прокидывается как есть (positionY, scrollBackConfig и т.п.).
 *
 * Роутер привязывается через bindLegacyHistory() из routing/router.ts —
 * без прямого импорта, чтобы не создавать цикл
 * legacyHistory → router → routeTree.gen → $.tsx → Router.tsx → legacyHistory.
 */
// Фаза 5: пакет history удалён — типы собственные (compat/types.ts)
import type { History, Location } from './compat/types';
import type { AnyRouter } from '@tanstack/react-router';

import { getRequestScope } from '../ssrRequestScope';

interface PathParts {
  pathname?: string;
  search?: string;
  hash?: string;
  state?: unknown;
}

/**
 * ЭТАП 2 — привязка per-request: на сервере роутер живёт в контексте ЗАПРОСА
 * (AsyncLocalStorage, см. ssrRequestScope.ts), иначе параллельные запросы
 * перетирали бы друг другу module-level переменную и рендер уходил бы по
 * ЧУЖОМУ location (компоненты compat/useLocation и серверный шим location
 * читают именно отсюда). Модульная переменная — fallback для клиента.
 */
let boundRouter: AnyRouter | null = null;

let lastAction: 'PUSH' | 'REPLACE' | 'POP' = 'POP';

const POP_ACTION_TYPES = ['BACK', 'FORWARD', 'GO'];

const trackPopAction = ({ action }: { action: { type: string } }): void => {
  if (POP_ACTION_TYPES.includes(action.type)) {
    lastAction = 'POP';
  }
};

const getBoundRouter = (): AnyRouter | null =>
  (getRequestScope()?.boundRouter as AnyRouter | undefined) ?? boundRouter;

export const bindLegacyHistory = (router: AnyRouter): void => {
  const scope = getRequestScope();
  if (scope) {
    scope.boundRouter = router;
    return;
  }
  boundRouter = router;
  if (typeof window !== 'undefined') {
    router.history.subscribe(trackPopAction);
  }
};

const tsHistory = (): AnyRouter['history'] => {
  const router = getBoundRouter();
  if (!router) {
    throw new Error(
      '[legacyHistory] router не привязан — bindLegacyHistory() должен вызваться в routing/router.ts до первого рендера'
    );
  }
  return router.history;
};

const ensurePrefix = (value: string | undefined, prefix: string): string => {
  if (!value || value === prefix) {
    return '';
  }
  return value.startsWith(prefix) ? value : `${prefix}${value}`;
};

/** Аналог parsePath из history v4: строка → { pathname?, search?, hash? }. */
const parsePath = (path: string): PathParts => {
  let rest = path;
  let hash: string | undefined;
  let search: string | undefined;

  const hashIndex = rest.indexOf('#');
  if (hashIndex !== -1) {
    hash = rest.substring(hashIndex);
    rest = rest.substring(0, hashIndex);
  }
  const searchIndex = rest.indexOf('?');
  if (searchIndex !== -1) {
    search = rest.substring(searchIndex);
    rest = rest.substring(0, searchIndex);
  }

  return {
    pathname: rest || undefined,
    search,
    hash
  };
};

const currentLocation = (): Location<any> => {
  const raw = tsHistory().location;
  const state = raw.state as any;

  return {
    pathname: raw.pathname,
    search: ensurePrefix(raw.search, '?'),
    hash: ensurePrefix(raw.hash, '#'),
    state,
    key: state?.key
  };
};

/**
 * Разрешение location-descriptor'а в абсолютный href по правилам v4:
 * отсутствующий/пустой pathname → текущий; относительный pathname →
 * резолвится от текущего; отсутствующие search/hash → '' (сброс).
 */
const resolveHref = (to: string | PathParts): string => {
  const parts: PathParts = typeof to === 'string' ? parsePath(to) : to;
  const cur = currentLocation();

  let pathname = parts.pathname ?? '';
  if (!pathname) {
    pathname = cur.pathname;
  } else if (!pathname.startsWith('/')) {
    // относительный путь — в приложении не встречается, но резолвим честно
    const base = cur.pathname.endsWith('/') ? cur.pathname : `${cur.pathname}/`;
    pathname = new URL(pathname, `http://x${base}`).pathname;
  }

  return `${pathname}${ensurePrefix(parts.search, '?')}${ensurePrefix(
    parts.hash,
    '#'
  )}`;
};

const resolveState = (
  to: string | PathParts,
  state?: unknown
): unknown | undefined => {
  if (typeof to === 'object' && to.state !== undefined) {
    return to.state;
  }
  return state;
};

/**
 * v4-паритет (БАГФИКС 08.08.2026): history.push/replace в v4 НИКОГДА не
 * скроллили — страницами управляют их собственные эффекты (ProductPage
 * scrollTo top, Catalog по смене id/фильтров и т.п.). TanStack же по
 * умолчанию делает scrollTo(0,0) в onRendered на КАЖДУЮ навигацию
 * (resetScroll=true), а мы идём мимо router.navigate (сырой history —
 * байт-в-байт URL без пересериализации search), поэтому опцию
 * resetScroll: false передать некуда — гасим флаг напрямую. Это ловит
 * и «Показать ещё» в каталоге (push того же компонента не должен
 * прыгать в топ). Флаг самовосстанавливается в onRendered (scroll.next
 * = true), так что back/forward-восстановление не затрагивается.
 * _scroll — приватное поле router-core; проверяется e2e-тестом
 * scroll restore в commerce.spec.ts при обновлении @tanstack/*.
 */
const suppressRouterScrollReset = (): void => {
  const scroll = (getBoundRouter() as { _scroll?: { next: boolean } } | null)
    ?._scroll;
  if (scroll) {
    scroll.next = false;
  }
};

const push = (to: string | PathParts, state?: unknown): void => {
  lastAction = 'PUSH';
  tsHistory().push(resolveHref(to), resolveState(to, state) as any);
  suppressRouterScrollReset();
};

const replace = (to: string | PathParts, state?: unknown): void => {
  lastAction = 'REPLACE';
  tsHistory().replace(resolveHref(to), resolveState(to, state) as any);
  suppressRouterScrollReset();
};

export const legacyHistory = {
  get length(): number {
    // ЭТАП 1 · Шаг 3 — SSR-safe: на сервере window нет.
    if (typeof window === 'undefined') {
      return 0;
    }
    return window.history.length;
  },
  get action(): 'PUSH' | 'REPLACE' | 'POP' {
    return lastAction;
  },
  get location(): Location<any> {
    return currentLocation();
  },
  push,
  replace,
  go: (n: number): void => {
    lastAction = 'POP';
    tsHistory().go(n);
  },
  goBack: (): void => {
    lastAction = 'POP';
    tsHistory().back();
  },
  goForward: (): void => {
    lastAction = 'POP';
    tsHistory().forward();
  },
  listen: (
    listener: (location: Location<any>, action: string) => void
  ): (() => void) => {
    // ВАЖНО: дедупликация по href. @tanstack/history шлёт notify на КАЖДЫЙ
    // push/replace, включая внутренние same-URL replace'ы роутера (сидинг
    // __TSR-state, load-цикл). Легаси-слушатели (initCartStore, метрика,
    // RRv5 setState) рассчитаны на события только при РЕАЛЬНОЙ смене URL —
    // без дедупа они входят в резонанс: initCartStore → рефетч корзины →
    // autorun CatalogBtn → лавина запросов и Maximum call stack
    // (наблюдалось 27.07.2026 на '/#login'). State-only replace'ов с тем же
    // href в приложении нет (проверено grep'ом) — дедуп ничего не теряет.
    const hrefOf = (): string => {
      const l = tsHistory().location;
      return `${l.pathname}${ensurePrefix(l.search, '?')}${ensurePrefix(
        l.hash,
        '#'
      )}`;
    };
    let lastHref = hrefOf();

    return tsHistory().subscribe((event?: any) => {
      const href = hrefOf();
      if (href === lastHref) {
        return;
      }
      lastHref = href;
      const action: string =
        event?.action?.type?.toUpperCase?.() ?? lastAction;
      listener(currentLocation(), action);
    });
  },
  createHref: (to: string | PathParts): string => resolveHref(to),
  // history.block/Prompt в приложении не используются (проверено 27.07.2026)
  block: (): (() => void) => () => {}
} as unknown as History<any>;
